import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { useAuth } from '../contexts/AuthContext';
import {
  EyeIcon,
  EyeSlashIcon,
  UserCircleIcon,
  ShieldCheckIcon,
  KeyIcon,
  CheckBadgeIcon,
  BuildingOfficeIcon,
} from '@heroicons/react/24/outline';
import toast from 'react-hot-toast';

const Profile = () => {
  const { user, tenantName, tenantId, updateProfile, changePassword } = useAuth();
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isUpdatingProfile, setIsUpdatingProfile] = useState(false);
  const [isChangingPassword, setIsChangingPassword] = useState(false);

  const {
    register: registerProfile,
    handleSubmit: handleProfileSubmit,
    formState: { errors: profileErrors },
  } = useForm({
    defaultValues: {
      firstName: user?.firstName || '',
      lastName: user?.lastName || '',
      phone: user?.phone || '',
    },
  });

  const {
    register: registerPassword,
    handleSubmit: handlePasswordSubmit,
    watch,
    reset: resetPasswordForm,
    formState: { errors: passwordErrors },
  } = useForm();

  const newPassword = watch('newPassword');

  const onProfileSubmit = async (data) => {
    setIsUpdatingProfile(true);
    try {
      const result = await updateProfile(data);
      if (result.success) {
        toast.success('Profile updated successfully!');
      }
    } catch (error) {
      console.error('Profile update error:', error);
    } finally {
      setIsUpdatingProfile(false);
    }
  };

  const onPasswordSubmit = async (data) => {
    setIsChangingPassword(true);
    try {
      const result = await changePassword(data.currentPassword, data.newPassword);
      if (result.success) {
        resetPasswordForm();
        toast.success('Password changed successfully!');
      }
    } catch (error) {
      console.error('Password change error:', error);
    } finally {
      setIsChangingPassword(false);
    }
  };

  return (
    <div className="space-y-8 animate-fade-in">
      {/* Header */}
      <div className="page-header">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary-500 to-primary-600 flex items-center justify-center shadow-orange">
            <UserCircleIcon className="w-5 h-5 text-white" />
          </div>
          <div>
            <h1 className="page-title">Profile Settings</h1>
            <p className="text-sm text-neutral-500">Manage your account information and security</p>
          </div>
        </div>
      </div>

      {/* Organization Info */}
      {tenantName && (
        <div className="card bg-gradient-to-br from-white to-primary-50/30 border border-primary-100">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
              <BuildingOfficeIcon className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-800">Organization</h3>
              <p className="text-sm text-neutral-500">Your tenant information</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <div className="p-4 bg-white rounded-lg border border-neutral-100">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Organization Name</dt>
              <dd className="mt-1 text-sm font-semibold text-neutral-800">{tenantName}</dd>
            </div>
            <div className="p-4 bg-white rounded-lg border border-neutral-100">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Your Role</dt>
              <dd className="mt-1 text-sm font-semibold text-neutral-800 capitalize">{user?.role}</dd>
            </div>
            <div className="p-4 bg-white rounded-lg border border-neutral-100">
              <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Email Verified</dt>
              <dd className="mt-1 flex items-center gap-1">
                {user?.emailVerified ? (
                  <>
                    <CheckBadgeIcon className="w-5 h-5 text-green-500" />
                    <span className="text-sm font-medium text-green-600">Verified</span>
                  </>
                ) : (
                  <span className="text-sm font-medium text-neutral-500">Not verified</span>
                )}
              </dd>
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-2">
        {/* Profile Information */}
        <div className="card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-primary-50 flex items-center justify-center">
              <UserCircleIcon className="w-5 h-5 text-primary-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-800">Profile Information</h3>
              <p className="text-sm text-neutral-500">Update your personal details</p>
            </div>
          </div>

          <form onSubmit={handleProfileSubmit(onProfileSubmit)} className="space-y-4">
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label htmlFor="firstName" className="input-label">
                  First Name
                </label>
                <input
                  type="text"
                  id="firstName"
                  className={`input-field ${profileErrors.firstName ? 'input-error' : ''}`}
                  {...registerProfile('firstName', {
                    required: 'First name is required',
                    minLength: {
                      value: 2,
                      message: 'Min 2 characters',
                    },
                  })}
                />
                {profileErrors.firstName && (
                  <p className="input-error-message">{profileErrors.firstName.message}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="input-label">
                  Last Name
                </label>
                <input
                  type="text"
                  id="lastName"
                  className={`input-field ${profileErrors.lastName ? 'input-error' : ''}`}
                  {...registerProfile('lastName', {
                    required: 'Last name is required',
                    minLength: {
                      value: 2,
                      message: 'Min 2 characters',
                    },
                  })}
                />
                {profileErrors.lastName && (
                  <p className="input-error-message">{profileErrors.lastName.message}</p>
                )}
              </div>
            </div>

            <div>
              <label htmlFor="email" className="input-label">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={user?.email || ''}
                disabled
                className="input-field bg-neutral-50 text-neutral-500 cursor-not-allowed"
              />
              <p className="input-helper">Email cannot be changed</p>
            </div>

            <div>
              <label htmlFor="phone" className="input-label">
                Phone Number
              </label>
              <input
                type="tel"
                id="phone"
                className="input-field"
                placeholder="+27 12 345 6789"
                {...registerProfile('phone')}
              />
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isUpdatingProfile}
                className="btn-primary w-full"
              >
                {isUpdatingProfile ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="spinner spinner-sm border-white/30 border-t-white" />
                    <span>Updating...</span>
                  </div>
                ) : (
                  'Update Profile'
                )}
              </button>
            </div>
          </form>
        </div>

        {/* Change Password */}
        <div className="card">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-10 h-10 rounded-lg bg-red-50 flex items-center justify-center">
              <KeyIcon className="w-5 h-5 text-red-500" />
            </div>
            <div>
              <h3 className="text-lg font-semibold text-neutral-800">Change Password</h3>
              <p className="text-sm text-neutral-500">Update your security credentials</p>
            </div>
          </div>

          <form onSubmit={handlePasswordSubmit(onPasswordSubmit)} className="space-y-4">
            <div>
              <label htmlFor="currentPassword" className="input-label">
                Current Password
              </label>
              <div className="relative">
                <input
                  type={showCurrentPassword ? 'text' : 'password'}
                  id="currentPassword"
                  className={`input-field pr-12 ${passwordErrors.currentPassword ? 'input-error' : ''}`}
                  placeholder="Enter current password"
                  {...registerPassword('currentPassword', {
                    required: 'Current password is required',
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600 transition-colors"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                >
                  {showCurrentPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {passwordErrors.currentPassword && (
                <p className="input-error-message">{passwordErrors.currentPassword.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="newPassword" className="input-label">
                New Password
              </label>
              <div className="relative">
                <input
                  type={showNewPassword ? 'text' : 'password'}
                  id="newPassword"
                  className={`input-field pr-12 ${passwordErrors.newPassword ? 'input-error' : ''}`}
                  placeholder="Enter new password"
                  {...registerPassword('newPassword', {
                    required: 'New password is required',
                    minLength: {
                      value: 8,
                      message: 'Password must be at least 8 characters',
                    },
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600 transition-colors"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                >
                  {showNewPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {passwordErrors.newPassword && (
                <p className="input-error-message">{passwordErrors.newPassword.message}</p>
              )}
            </div>

            <div>
              <label htmlFor="confirmPassword" className="input-label">
                Confirm New Password
              </label>
              <div className="relative">
                <input
                  type={showConfirmPassword ? 'text' : 'password'}
                  id="confirmPassword"
                  className={`input-field pr-12 ${passwordErrors.confirmPassword ? 'input-error' : ''}`}
                  placeholder="Confirm new password"
                  {...registerPassword('confirmPassword', {
                    required: 'Please confirm your new password',
                    validate: (value) =>
                      value === newPassword || 'Passwords do not match',
                  })}
                />
                <button
                  type="button"
                  className="absolute inset-y-0 right-0 pr-4 flex items-center text-neutral-400 hover:text-neutral-600 transition-colors"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                >
                  {showConfirmPassword ? (
                    <EyeSlashIcon className="h-5 w-5" />
                  ) : (
                    <EyeIcon className="h-5 w-5" />
                  )}
                </button>
              </div>
              {passwordErrors.confirmPassword && (
                <p className="input-error-message">{passwordErrors.confirmPassword.message}</p>
              )}
            </div>

            <div className="pt-4">
              <button
                type="submit"
                disabled={isChangingPassword}
                className="btn-danger w-full"
              >
                {isChangingPassword ? (
                  <div className="flex items-center justify-center gap-2">
                    <div className="spinner spinner-sm border-white/30 border-t-white" />
                    <span>Changing...</span>
                  </div>
                ) : (
                  'Change Password'
                )}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Account Information */}
      <div className="card">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-lg bg-green-50 flex items-center justify-center">
            <ShieldCheckIcon className="w-5 h-5 text-green-500" />
          </div>
          <div>
            <h3 className="text-lg font-semibold text-neutral-800">Account Information</h3>
            <p className="text-sm text-neutral-500">Your account status and details</p>
          </div>
        </div>

        <dl className="grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          <div className="p-4 bg-neutral-50 rounded-lg">
            <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Account Status</dt>
            <dd className="mt-2">
              <span className={`badge ${
                user?.status === 'active' ? 'badge-green' : 'badge-red'
              }`}>
                {user?.status}
              </span>
            </dd>
          </div>
          <div className="p-4 bg-neutral-50 rounded-lg">
            <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Role</dt>
            <dd className="mt-2 text-sm font-semibold text-neutral-800 capitalize">{user?.role}</dd>
          </div>
          <div className="p-4 bg-neutral-50 rounded-lg">
            <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Member Since</dt>
            <dd className="mt-2 text-sm font-semibold text-neutral-800">
              {user?.createdAt ? new Date(user.createdAt).toLocaleDateString() : 'N/A'}
            </dd>
          </div>
          <div className="p-4 bg-neutral-50 rounded-lg">
            <dt className="text-xs font-medium text-neutral-500 uppercase tracking-wider">Email Verified</dt>
            <dd className="mt-2 flex items-center gap-1">
              {user?.emailVerified ? (
                <>
                  <CheckBadgeIcon className="w-5 h-5 text-green-500" />
                  <span className="text-sm font-medium text-green-600">Verified</span>
                </>
              ) : (
                <span className="text-sm font-medium text-neutral-500">Not verified</span>
              )}
            </dd>
          </div>
        </dl>
      </div>
    </div>
  );
};

export default Profile;
